// ── Server-authoritative reconcile ──────────────────────────────────────────
// On the auth path the SERVER is the sole resolver: `state.combat` (the mirror)
// is the truth, not a local engine. The combat terminal frame is a `match:diff`
// that sets the mirror's `combat` to null. If the non-acting client (the
// attacker, locked behind "Waiting for opponent...") DROPS that one diff and the
// opponent then idles, no later diff exposes the version gap, so the client
// never requests a Full and stays bricked forever (the reported Lugbog-cat bug).
//
// reconcileAuthCombat compares the client's locally-shown combat lock against the
// authoritative mirror and returns the ONE corrective action:
//
//   'none'         the lock matches the mirror (same live fight). Wait.
//   'clear'        the mirror has NO fight (or a DIFFERENT one) while the client
//                  still shows a non-acting lock for the old fight. The terminal
//                  frame was missed; re-fold the (null) mirror to tear the lock
//                  down. Idempotent: re-applying null clears nothing twice.
//   'request-full' the mirror still shows the SAME fight the client is locked on
//                  but it has not progressed for `stuckAfterMs` (no new frames).
//                  proactively pull a fresh Full so a pure lost-frame (no socket
//                  reconnect) self-heals instead of waiting for the opponent.
//
// `lock`   the local non-acting combat prompt { combatId, actingSeat } or null.
// `mirror` the authoritative mirror's `combat` record (null when resolved).
// `localSeat` the client's seat (a lock the LOCAL seat acts on is the actor's own
//             prompt. Never auto-cleared; only the non-acting waiting lock heals).
export function reconcileAuthCombat({ lock, mirrorCombat, localSeat, lockShownAt, now, stuckAfterMs }) {
  if (!lock || !lock.combatId) return 'none';
  // The actor owns its own prompt; only a non-acting WAITING lock self-heals.
  if (lock.actingSeat && lock.actingSeat === localSeat) return 'none';

  const mirrorId = mirrorCombat?.combatId || null;
  // Mirror resolved this fight (null) or moved to a different one → the lock is an
  // orphan; the terminal frame was dropped. Tear it down from the mirror.
  if (mirrorId !== lock.combatId) return 'clear';

  // Same fight still live in the mirror: only escalate to a Full pull if the lock
  // has been shown long enough with no progress (a genuine stall, not a fresh
  // mid-fight wait the opponent is still deciding).
  if (lockShownAt != null && (now - lockShownAt) >= stuckAfterMs) return 'request-full';
  return 'none';
}
